import type { ComponentType } from "react";

/**
 * The gate for a page nobody needs permission to open.
 *
 * Spelled out rather than left blank on purpose. Thirteen nexpim pages once
 * shipped with no gate at all, and every one of them was an omission nobody
 * noticed, because an absent field looks exactly like a deliberate one. A page
 * that should be open to anyone signed in now has to say so, which a reviewer
 * can see and question.
 */
export const PUBLIC_VIEW = "PUBLIC";

interface NavEntry {
  /** Translation key, e.g. `navigation.item.artworks`. */
  labelKey: string;
  icon?: ComponentType<{ className?: string }>;
}

/** A page. Carries a route, and therefore must declare who may open it. */
export interface NavLink extends NavEntry {
  to: string;
  /**
   * View code required to see this entry. ROOT bypasses; PUBLIC_VIEW opens it.
   *
   * A list means "any of these", which a page that merges several permissioned
   * screens needs: manual invoices is one entry covering three feeds, and an
   * account with only one of them should still see the link.
   */
  view: string | string[];
  children?: never;
}

/** A pure grouping entry. Has no route of its own, so it gates nothing. */
export interface NavGroup extends NavEntry {
  to?: never;
  /** Optional: hides the whole group, on top of whatever its children say. */
  view?: string | string[];
  /**
   * Starts expanded. For the one or two sections a user lives in all day,
   * where collapsing by default costs a click on every visit and buys nothing.
   */
  defaultOpen?: boolean;
  children: NavItem[];
}

export type NavItem = NavLink | NavGroup;

export interface NavSection {
  items: NavItem[];
}

/**
 * Filters a nav tree against the account's view codes.
 *
 * Matching `RouteAccessChecker`, an entry the user can't reach is *removed*,
 * not disabled — an empty parent disappears with its children rather than
 * hanging around as a dead expander.
 */
export function visibleNav(items: NavItem[], hasView: (code: string) => boolean): NavItem[] {
  const result: NavItem[] = [];

  const permitted = (view: string | string[] | undefined) => {
    if (!view) return true;
    const codes = Array.isArray(view) ? view : [view];
    return codes.some((code) => code === PUBLIC_VIEW || hasView(code));
  };

  for (const item of items) {
    if (!permitted(item.view)) continue;

    if (item.children?.length) {
      const children = visibleNav(item.children, hasView);
      if (children.length === 0) continue;
      result.push({ ...item, children });
    } else {
      result.push(item);
    }
  }

  return result;
}

/**
 * The one nav link that should look active for a given path.
 *
 * Neither of the obvious rules works alone. Exact matching leaves Products
 * unhighlighted while you are on /products/2344, which has no nav entry of its
 * own. Prefix matching lights up every ancestor, so standing on
 * /pricing/product/rule highlights /pricing/product too.
 *
 * The rule that does work is longest match wins: a link is active when the
 * path is it or sits beneath it, and no other link matches more of the path.
 * The boundary check keeps /product from claiming /products.
 */
export function activeNavPath(items: NavItem[], pathname: string): string | undefined {
  let best: string | undefined;

  const walk = (entries: NavItem[]) => {
    for (const entry of entries) {
      if (entry.to) {
        const matches = pathname === entry.to || pathname.startsWith(`${entry.to}/`);
        if (matches && (best === undefined || entry.to.length > best.length)) {
          best = entry.to;
        }
      }
      if (entry.children) walk(entry.children);
    }
  };

  walk(items);
  return best;
}
